import './fundraiser.css';

import EventEmitter from "eventemitter3";
import { Modal } from '../modal';
import { Util } from '../../../utils/util';
import { FieldType, Modals } from '../../../types/enums';
import { Order } from '../../../models/order';
import { IFundraiser, IFundraiserProps } from './fundraiser.interface';
import { TwoWayBindingHelper } from '../../../utils/two-way-binding';
import { TimedMessage } from '../../shared/TimedMessage/timed-message';
import { EventTypes } from '../../../core/event-bus';
import { IName } from '../../../interfaces/global.interfaces';
import { Context } from '../../../models/context';
import { DataLayerService } from '../../../services/data-layer-service';
import { LocalizationService } from '../../../services/localization-service';
import { InputValidator } from '../../../validators/input-validator';

/**
 * Represents the Fundraiser modal, allowing users to apply a fundraiser to their order.
 */
export class Fundraiser extends Modal {
    private readonly _order: Order;
    private readonly _item: IFundraiser;
    private readonly _bindingHelper: TwoWayBindingHelper<IFundraiser>;
    private _options?: IFundraiserProps;

    /**
     * Initializes the Fundraiser modal.
     * @param {EventEmitter} eventBus - The event bus for handling events.
     * @param {Order} order - The order service instance.
     */
    constructor(eventBus: EventEmitter, context: Context, dataLayer: DataLayerService, localizationService: LocalizationService, order: Order) {
        super("modal_fundraiser", eventBus, context, dataLayer, localizationService, { hasCloseButton: true, ariaLabel: "Fundraiser" }, Modals.FUNDRAISER);

        this._order = order;
        this._item = this._initializeModel();

        this._bindingHelper = new TwoWayBindingHelper(this._item);
        this._setupTwoWayBinding();
        this._setEventListeners();
        this._setEventBusListener();
    }

    /**
     * Initializes the fundraiser model.
     * @private
     * @returns {IFundraiser} The initial fundraiser object.
     */
    private _initializeModel(): IFundraiser {
        return {
            fundraiserCode: "",
            fundraiser: null,
            customField1: "",
            customField2: ""
        };
    }

    /**
     * Sets up two-way data binding for fundraiser custom fields.
     * @private
     */
    private _setupTwoWayBinding(): void {
        this._bindingHelper.bindToInput("customField1", "txt_fundraiser_first_custom_field");
        this._bindingHelper.bindToInput("customField2", "txt_fundraiser_second_custom_field");
    }

    /**
     * Attaches event listeners for button clicks.
     * @protected
     */
    protected _setEventListeners(): void {
        Util.setElement("onclick", "btn_fundraiser_save", this._handleSave);
        Util.setElement("onclick", "btn_fundraiser_cancel", this.close);
    }

    /**
     * Listens for modal-related events from the event bus.
     * @protected
     */
    protected _setEventBusListener = (): void => {
        this._eventBus.on(EventTypes.MODAL_OPEN, (modal: Modals, options?: IFundraiserProps) => {
            if (modal === Modals.FUNDRAISER)
                this._options = options;
        });
    }

    /**
     * Opens the Fundraiser modal and initializes the page.
     */
    public override open(): void {
        this._configureOptions();
        this.init();
    }

    /**
     * Configures options based on the provided fundraiser.
     * @private
     */
    private _configureOptions() {
        this._item.fundraiser = this._options?.fundraiser || null;
    }

    /**
     * Initializes the modal by clearing errors and rendering the page.
     */
    public override init(): void {
        super.init();
        this._hideErrorElements();
        this._renderPage();
    }

    /**
     * Renders the fundraiser modal page.
     * @private
     */
    private _renderPage(): void {
        if (!this._item.fundraiser)
            return;

        if (!this._item.fundraiser.IsCustomField1 && !this._item.fundraiser.IsCustomField2) {
            this._handleSave();
            return;
        }

        Util.setElement("innerText", "lbl_fundraiser_title", this._item.fundraiser.Name);

        this._toggleFundraiserField("first", this._item.fundraiser.IsCustomField1, this._item.fundraiser.CustomField1Names);
        this._toggleFundraiserField("second", this._item.fundraiser.IsCustomField2, this._item.fundraiser.CustomField2Names);
        super.open();
    }

    /**
     * Updates the display of a fundraiser's custom fields.
     * @param {string} fieldKey - The custom field key (e.g., 'first' or 'second').
     * @param {boolean} isVisible - Whether the field should be visible.
     * @param {IName[]} fieldNames - The localized names of the field.
     */
    private _toggleFundraiserField(fieldKey: string, isVisible: boolean, fieldNames: IName[]): void {
        const fieldName = this._localizationService.GetName(fieldNames);
        const errorMessage = this._localizationService.names("ENTER_FUNDRAISER_NAME").replace("{{fundraiser_name}}", fieldName);

        Util.setElement("innerText", `lbl_fundraiser_${fieldKey}_custom_field`, fieldName);
        Util.setElement("innerText", `spn_fundraiser_${fieldKey}_custom_field_error`, errorMessage);

        isVisible ? Util.showElement(`div_fundraiser_${fieldKey}_custom_field`)
            : Util.hideElement(`div_fundraiser_${fieldKey}_custom_field`);
    }

    /**
     * Handles saving the fundraiser selection.
     * Validates the input and applies the fundraiser to the order.
     * @private
     */
    private _handleSave = (): void => {
        if (!this._validateFundraiserInputs() || !this._item.fundraiser)
            return;

        this._order.setFundraiser(this._item.fundraiser.FundraiserId, this._item.customField1 || undefined, this._item.customField2 || undefined);

        const msg = new TimedMessage(this._localizationService.names("FUNDRAISER_CODE_APPLIED"), undefined, 1000);
        msg.show();

        this.close();
    }

    /**
     * Hides error messages related to fundraiser input fields.
     * @private
     */
    private _hideErrorElements(): void {
        Util.hideElementErrors("txt_fundraiser_first_custom_field", "spn_fundraiser_first_custom_field_error");
        Util.hideElementErrors("txt_fundraiser_second_custom_field", "spn_fundraiser_second_custom_field_error");
    }

    /**
     * Validates the fundraiser input fields.
     * @private
     * @returns {boolean} Returns `true` if validation passes, otherwise `false`.
     */
    private _validateFundraiserInputs(): boolean {
        this._hideErrorElements();

        const fields: { type: FieldType, inputId: string, errorId: string }[] = [];

        if (this._item.fundraiser?.IsCustomField1 && this._item.customField1 === "")
            fields.push({ type: FieldType.FAILED, inputId: "txt_fundraiser_first_custom_field", errorId: "spn_fundraiser_first_custom_field_error" });

        if (this._item.fundraiser?.IsCustomField2 && this._item.customField2 === "")
            fields.push({ type: FieldType.FAILED, inputId: "txt_fundraiser_second_custom_field", errorId: "spn_fundraiser_second_custom_field_error" });

        if (fields.length == 0)
            return true;

        return !fields.map(field => InputValidator.validate(field.type, field.inputId, field.errorId))
            .some(result => result);
    }

    /**
     * Closes the modal.
     */
    public override close = (): void => {
        super.close();
    }
}